import { CarrierType, NumericReleaseResponseDto, OrderStatus } from '@planning/app/api'
import { isValidityExpiredByNow } from '@planning/utils/release-validity-utils'
import { EmptyIndicator } from '@tom-ui/ui'
import _ from 'lodash'
import { computed, makeObservable } from 'mobx'
import { EntityItem } from '../base/EntityItem'
import { ICompanyItem } from '../externalDriver/CompanyItem'
import { CompanyItemStore } from '../externalDriver/CompanyItemStore'
import { IOrderItem } from '../order/OrderItem'
import { OrderItemStore } from '../order/OrderItemStore'
import { RailVisitItemStore } from '../railVisit/RailVisitItemStore'
import { IElementWithId, IEntity } from '../types'
import { VesselVisitItemStore } from '../vesselVisit/VesselVisitItemStore'

export interface IReleaseItem<T extends IElementWithId<number>> extends IEntity<T> {
  orders: IOrderItem[]
  operator?: ICompanyItem | null
  transportCompany?: ICompanyItem | null
  carrierName: string
  canEdit: boolean
  canDelete: boolean
  assigned: number
  fulfilled: number
  isExpired: boolean
}

export interface INumericReleaseItem
  extends IEntity<NumericReleaseResponseDto>, IReleaseItem<NumericReleaseResponseDto> {}

export class NumericReleaseItem
  extends EntityItem<NumericReleaseResponseDto>
  implements INumericReleaseItem
{
  constructor(
    id: number,
    data: NumericReleaseResponseDto,
    private readonly orderItemStore?: OrderItemStore,
    private readonly companyItemStore?: CompanyItemStore,
    private readonly vesselVisitItemStore?: VesselVisitItemStore,
    private readonly railVisitItemStore?: RailVisitItemStore,
  ) {
    super(id, data)
    makeObservable(this, {
      orders: computed,
      operator: computed,
      canDelete: computed,
      canEdit: computed,
      carrierName: computed,
      assigned: computed,
      fulfilled: computed,
      isExpired: computed,
      transportCompany: computed,
    })
  }

  get orders() {
    return _.get(this.orderItemStore?.ordersByNumericReleaseId, this.id) ?? []
  }

  get operator() {
    return _(this.companyItemStore?.elements).find(x => x.data.id === this.data.shippingLineId)
  }

  get carrierName() {
    switch (this.data.modeOfTransport) {
      case CarrierType.Truck:
        return this.transportCompany?.data.name ?? 'TRUCK'
      case CarrierType.Train:
        return (
          _(this.railVisitItemStore?.elements).find(x => x.data.id === this.data.carrierVisitId)
            ?.data.identifier ?? EmptyIndicator
        )
      case CarrierType.Vessel:
        return (
          _(this.vesselVisitItemStore?.elements).find(x => x.data.id === this.data.carrierVisitId)
            ?.data.identifier ?? EmptyIndicator
        )
      default:
        return EmptyIndicator
    }
  }

  get transportCompany() {
    if (this.data.modeOfTransport === CarrierType.Truck && this.data.transportCompanyId) {
      return _(this.companyItemStore?.elements).find(
        x => x.data.id === this.data.transportCompanyId,
      )
    }
  }

  get canDelete() {
    return !!this.id && this.assigned === 0
  }

  get canEdit() {
    return !!this.id && this.assigned === 0
  }

  get assigned() {
    return this.data.assignedOrders?.length ?? 0
  }

  get fulfilled() {
    return (
      this.data.assignedOrders?.filter(order => order.status === OrderStatus.Fulfilled).length ?? 0
    )
  }

  get isExpired() {
    return isValidityExpiredByNow(this.data.validityDate)
  }
}
